import React, { useCallback, useEffect, useRef, useState } from "react";
import { TextMessage } from "./TextMessage";
import { ProductMessage } from "./ProductMessage";
import { IS_AT_BOTTOM_OFFSET } from "@/configs/constants";
import { Product } from "@/types/product";
import { UnifiedMessage } from "@/hooks/use-messages";

interface ChatMessagesProps {
  messages: UnifiedMessage[];
  setProductDescription: (product: Product) => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  setProductDescription,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showNewMessagesPrompt, setShowNewMessagesPrompt] =
    React.useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessagesCount, setNewMessagesCount] = React.useState(0);
  const prevMessagesLength = React.useRef(messages.length);
  const prevScrollHeight = React.useRef(0);
  const prevScrollTop = React.useRef(0);
  //
  // Record scroll position before messages update
  useEffect(() => {
    const refElement = scrollRef.current;
    if (!refElement) return;

    prevScrollHeight.current = refElement.scrollHeight;
    prevScrollTop.current = refElement.scrollTop;
  }, [messages.length]);

  useEffect(() => {
    const refElement = scrollRef.current;
    if (!refElement) return;

    const isAtBottom = () =>
      refElement.scrollHeight - refElement.scrollTop - refElement.clientHeight <
      IS_AT_BOTTOM_OFFSET;

    let userAtBottom = true;

    const handleUserScroll = () => {
      userAtBottom = isAtBottom();
      if (userAtBottom) {
        setIsAtBottom(true);
        setShowNewMessagesPrompt(false);
      } else {
        setIsAtBottom(false);
      }
    };

    refElement.addEventListener("scroll", handleUserScroll);

    // On mount, scroll to bottom
    refElement.scrollTop = refElement.scrollHeight;
    setIsAtBottom(true);

    return () => {
      refElement.removeEventListener("scroll", handleUserScroll);
    };
  }, []);

  useEffect(() => {
    const refElement = scrollRef.current;
    if (!refElement) return;

    if (isAtBottom) {
      refElement.scrollTop = refElement.scrollHeight;
      setShowNewMessagesPrompt(false);
      setNewMessagesCount(0);
    } else if (messages.length > prevMessagesLength.current) {
      const scrollDiff = refElement.scrollHeight - prevScrollHeight.current;

      // Adjust scrollTop to keep the view stationary
      refElement.scrollTop = prevScrollTop.current + scrollDiff;
      setShowNewMessagesPrompt(true);
      setNewMessagesCount(
        (count) => count + (messages.length - prevMessagesLength.current),
      );
    } else {
      setShowNewMessagesPrompt(true);
    }

    prevMessagesLength.current = messages.length;
  }, [messages, isAtBottom]);

  const handleScrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }

    setShowNewMessagesPrompt(false);
    setNewMessagesCount(0);
  }, []);

  return (
    <div className="flex relative flex-1 overflow-hidden pt-[20px] px-[0px]">
      <div className="flex-1 overflow-y-auto" ref={scrollRef}>
        <div className="overflow-y-auto">
          {messages.map((message) => {
            const isTextMessage =
              !message.message?.startsWith("products_") &&
              !message.text?.startsWith("products_");

            return isTextMessage ? (
              <TextMessage key={message.id} message={message} />
            ) : (
              <ProductMessage
                key={message.id}
                message={message}
                setProductDescription={setProductDescription}
              />
            );
          })}
        </div>
      </div>
      <div
        className={`absolute bottom-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded cursor-pointer shadow whitespace-nowrap transition-opacity ${
          showNewMessagesPrompt ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleScrollToBottom}
      >
        {newMessagesCount > 0 ? `See recent messages` : "Scroll to bottom"}
      </div>
    </div>
  );
};
export default ChatMessages;
